import { useMemo, useCallback } from "react";
import { Plot } from "@/types/plot";
import { EntryCollection } from "@/types/entryCollection";

type DisplayMode = "grid" | "list";
type PlotNavigationTarget = Pick<Plot, "plotID"> & { id?: string };

export function entryCollectionBelongsToPlot(
  entryCollection: EntryCollection,
  plot: PlotNavigationTarget
): boolean {
  const entryCollectionPlotRefId =
    typeof entryCollection.plotRef === "string" ? entryCollection.plotRef : entryCollection.plotRef?.id;

  if (!entryCollectionPlotRefId) return false;
  if (plot.id && entryCollectionPlotRefId === plot.id) return true;

  // Fall back to plotID so tests/mocks that use plot numbers as ref ids still work.
  return entryCollectionPlotRefId === plot.plotID;
}

export interface PlotNavigationResult {
  orderedPlots: Plot[];

  // Adjacent plots
  prevPlot: Plot | null;
  nextPlot: Plot | null;
  upPlot: Plot | null; // Grid mode only
  downPlot: Plot | null; // Grid mode only

  // State
  canGoPrev: boolean;
  canGoNext: boolean;
  canGoUp: boolean;
  canGoDown: boolean;

  // Callbacks
  goToPrev: () => void;
  goToNext: () => void;
  goToUp: () => void;
  goToDown: () => void;
  goToPlot: (plot: Plot) => void;

  // Position
  currentIndex: number;
  totalPlots: number;
  displayMode: DisplayMode;

  // Get entry collections for a specific plot (for navigation/preloading)
  getEntriesForPlot: (plot: PlotNavigationTarget) => EntryCollection[];
}

interface UsePlotNavigationParams {
  plots: Plot[];
  entryCollections: EntryCollection[];
  currentPlot: Plot | null;
  onNavigate: (plot: Plot, entryCollections: EntryCollection[]) => void;
}

/**
 * Hook to manage plot navigation within PlotDetailsSheet.
 * Handles both grid mode (row-first ordering with vertical navigation)
 * and list mode (sequential by plotMapUploadPosition then plotID).
 */
export function usePlotNavigation({
  plots,
  entryCollections,
  currentPlot,
  onNavigate,
}: UsePlotNavigationParams): PlotNavigationResult {
  // Determine display mode based on percentage of plots with valid row/column data
  // Falls back to list mode if the current plot lacks grid coordinates
  const displayMode: DisplayMode = useMemo(() => {
    if (plots.length === 0) return "grid";
    const plotsWithGrid = plots.filter((p) => p.row !== undefined && p.column !== undefined);
    const gridPercentage = plotsWithGrid.length / plots.length;
    const preferGrid = gridPercentage > 0.5;

    // If we prefer grid mode but the current plot lacks coordinates, fall back to list
    if (preferGrid && currentPlot && (currentPlot.row === undefined || currentPlot.column === undefined)) {
      return "list";
    }

    return preferGrid ? "grid" : "list";
  }, [plots, currentPlot]);

  // Sort plots based on display mode
  const sortedPlots = useMemo(() => {
    if (displayMode === "grid") {
      // Grid mode: row-first ordering (across row, then down)
      return [...plots]
        .filter((p) => p.row !== undefined && p.column !== undefined)
        .sort((a, b) => {
          if (a.row !== b.row) return a.row! - b.row!;
          return a.column! - b.column!;
        });
    } else {
      // List mode: sequential by plotMapUploadPosition then plotID
      return [...plots].sort((a, b) => {
        const posA = a.plotMapUploadPosition ?? Number.MAX_SAFE_INTEGER;
        const posB = b.plotMapUploadPosition ?? Number.MAX_SAFE_INTEGER;
        if (posA !== posB) return posA - posB;
        return a.plotID.localeCompare(b.plotID, undefined, { numeric: true });
      });
    }
  }, [plots, displayMode]);

  // Find current plot index in sorted array
  const currentIndex = useMemo(() => {
    if (!currentPlot) return -1;
    return sortedPlots.findIndex((p) => p.plotID === currentPlot.plotID);
  }, [sortedPlots, currentPlot]);

  // Calculate adjacent plots for horizontal (left/right) navigation
  const { prevPlot, nextPlot } = useMemo(() => {
    if (currentIndex === -1) {
      return { prevPlot: null, nextPlot: null };
    }
    return {
      prevPlot: currentIndex > 0 ? sortedPlots[currentIndex - 1] : null,
      nextPlot: currentIndex < sortedPlots.length - 1 ? sortedPlots[currentIndex + 1] : null,
    };
  }, [sortedPlots, currentIndex]);

  // Calculate vertical navigation (grid mode only - same column, different row)
  const { upPlot, downPlot } = useMemo(() => {
    if (displayMode !== "grid" || !currentPlot || currentPlot.row === undefined || currentPlot.column === undefined) {
      return { upPlot: null, downPlot: null };
    }

    // Find plots in the same column, sorted by row
    const sameColumn = sortedPlots
      .filter((p) => p.column === currentPlot.column && p.row !== undefined && p.plotID !== currentPlot.plotID)
      .sort((a, b) => a.row! - b.row!);

    // upPlot: the plot with the largest row that's still less than current (closest above)
    const plotsAbove = sameColumn.filter((p) => p.row! < currentPlot.row!);
    const upPlot = plotsAbove.length > 0 ? plotsAbove[plotsAbove.length - 1] : null;

    // downPlot: the first plot with a row greater than current (closest below)
    const downPlot = sameColumn.find((p) => p.row! > currentPlot.row!) ?? null;

    return { upPlot, downPlot };
  }, [displayMode, currentPlot, sortedPlots]);

  // Helper to get entry collections for a specific plot
  const getEntriesForPlot = useCallback(
    (plot: PlotNavigationTarget): EntryCollection[] => {
      return entryCollections.filter((entryCollection) => entryCollectionBelongsToPlot(entryCollection, plot));
    },
    [entryCollections]
  );

  // Navigation callbacks
  const goToPrev = useCallback(() => {
    if (prevPlot) {
      onNavigate(prevPlot, getEntriesForPlot(prevPlot));
    }
  }, [prevPlot, onNavigate, getEntriesForPlot]);

  const goToNext = useCallback(() => {
    if (nextPlot) {
      onNavigate(nextPlot, getEntriesForPlot(nextPlot));
    }
  }, [nextPlot, onNavigate, getEntriesForPlot]);

  const goToUp = useCallback(() => {
    if (upPlot) {
      onNavigate(upPlot, getEntriesForPlot(upPlot));
    }
  }, [upPlot, onNavigate, getEntriesForPlot]);

  const goToDown = useCallback(() => {
    if (downPlot) {
      onNavigate(downPlot, getEntriesForPlot(downPlot));
    }
  }, [downPlot, onNavigate, getEntriesForPlot]);

  const goToPlot = useCallback(
    (plot: Plot) => {
      onNavigate(plot, getEntriesForPlot(plot));
    },
    [getEntriesForPlot, onNavigate]
  );

  return {
    orderedPlots: sortedPlots,
    prevPlot,
    nextPlot,
    upPlot,
    downPlot,
    canGoPrev: prevPlot !== null,
    canGoNext: nextPlot !== null,
    canGoUp: upPlot !== null,
    canGoDown: downPlot !== null,
    goToPrev,
    goToNext,
    goToUp,
    goToDown,
    goToPlot,
    currentIndex,
    totalPlots: sortedPlots.length,
    displayMode,
    getEntriesForPlot,
  };
}
